import { friendlyDbError } from '../utils/friendlyDbError';
import { create } from 'zustand';
import { supabase } from '../supabase';
import type { LeadNote, NoteAttachment } from '../types';

interface PartnerNoteState {
  notes: LeadNote[];
  loading: boolean;
  error: string | null;
  fetchNotesByPartnerId: (partnerId: string) => Promise<void>;
  createNote: (partnerId: string, content: string, organizationId: string, userId: string) => Promise<LeadNote | null>;
  updateNote: (noteId: string, content: string) => Promise<void>;
  deleteNote: (noteId: string) => Promise<void>;
}

export const usePartnerNoteStore = create<PartnerNoteState>((set, get) => ({
  notes: [],
  loading: false,
  error: null,

  fetchNotesByPartnerId: async (partnerId: string) => {
    set({ loading: true, error: null });
    try {
      const { data, error } = await supabase
        .from('deal_notes')
        .select('*')
        .eq('partner_id', partnerId)
        .order('created_at', { ascending: false })
        .limit(100);

      if (error) throw error;

      // Get unique user IDs from notes
      const userIds = [...new Set((data || []).map(note => note.user_id).filter(Boolean))];

      // Fetch author information for all users in a single query
      let authorsMap: Record<string, any> = {};
      if (userIds.length > 0) {
        const { data: authorsData, error: authorsError } = await supabase
          .from('user_profiles')
          .select('id, full_name, email, avatar_url, profile_image_url')
          .in('id', userIds);

        if (!authorsError && authorsData) {
          authorsMap = authorsData.reduce((acc, author) => {
            acc[author.id] = author;
            return acc;
          }, {} as Record<string, any>);
        }
      }

      // Fetch attachments for all notes
      const noteIds = (data || []).map(note => note.id);
      let attachmentsMap: Record<string, NoteAttachment[]> = {};
      if (noteIds.length > 0) {
        const { data: attachmentsData, error: attachmentsError } = await supabase
          .from('note_attachments')
          .select('*')
          .in('note_id', noteIds)
          .order('created_at', { ascending: true });

        if (!attachmentsError && attachmentsData) {
          attachmentsMap = attachmentsData.reduce((acc, attachment) => {
            if (!acc[attachment.note_id]) {
              acc[attachment.note_id] = [];
            }
            acc[attachment.note_id].push(attachment);
            return acc;
          }, {} as Record<string, NoteAttachment[]>);
        }
      }

      const author = (userId: string | null | undefined) => {
        const profile = userId ? authorsMap[userId] : null;
        if (!profile) {
          return {
            id: userId || 'unknown',
            email: 'Unknown User',
            full_name: 'Unknown User',
            profile_image_url: null,
          };
        }
        return {
          ...profile,
          profile_image_url: profile.avatar_url || profile.profile_image_url,
        };
      };

      const notesWithAuthors = (data || []).map(note => ({
        ...note,
        author: author(note.user_id),
        attachments: attachmentsMap[note.id] || [],
      }));

      set({ notes: notesWithAuthors, loading: false });
    } catch (error) {
      set({ error: friendlyDbError(error), loading: false });
    }
  },

  createNote: async (partnerId: string, content: string, organizationId: string, userId: string) => {
    try {
      const { data: profileData, error: profileError } = await supabase
        .from('user_profiles')
        .select('full_name, email, avatar_url, profile_image_url')
        .eq('id', userId)
        .single();

      if (profileError) throw profileError;

      // Il CHECK a DB richiede esattamente uno tra lead_id/deal_id/partner_id: qui
      // impostiamo solo partner_id, gli altri due restano non impostati.
      const insertData = {
        partner_id: partnerId,
        user_id: userId,
        content,
        organization_id: organizationId,
      };

      const { data, error } = await supabase
        .from('deal_notes')
        .insert(insertData)
        .select('*')
        .single();

      if (error) throw error;

      const newNote = {
        ...data,
        author: {
          id: userId,
          email: profileData?.email,
          full_name: profileData?.full_name || 'User',
          profile_image_url: profileData?.avatar_url || profileData?.profile_image_url,
        },
        attachments: [],
      };

      set({ notes: [newNote, ...get().notes] });
      return newNote;
    } catch (error) {
      set({ error: friendlyDbError(error) });
      return null;
    }
  },

  updateNote: async (noteId: string, content: string) => {
    try {
      const currentNote = get().notes.find(note => note.id === noteId);
      if (currentNote) {
        // Create version entry with current content before updating
        try {
          const { data: versionData, error: versionCountError } = await supabase
            .from('deal_note_versions')
            .select('version_number')
            .eq('note_id', noteId)
            .order('version_number', { ascending: false })
            .limit(1);

          if (versionCountError) throw versionCountError;

          const nextVersionNumber = versionData && versionData.length > 0 ?
            versionData[0].version_number + 1 : 1;

          const { error: versionError } = await supabase
            .from('deal_note_versions')
            .insert({
              note_id: noteId,
              content: currentNote.content,
              user_id: currentNote.user_id,
              version_number: nextVersionNumber,
            });

          if (versionError) {
            console.warn('Failed to create note version:', versionError);
          }
        } catch (versionError) {
          console.warn('Failed to create note version:', versionError);
        }
      }

      const { data, error } = await supabase
        .from('deal_notes')
        .update({
          content,
          updated_at: new Date().toISOString(),
          is_edited: true,
          edit_count: (currentNote?.edit_count || 0) + 1,
        })
        .eq('id', noteId)
        .select('*')
        .single();

      if (error) throw error;

      const updatedNote = {
        ...data,
        author: currentNote?.author || {
          id: data.user_id,
          email: 'Unknown User',
          full_name: 'Unknown User',
          profile_image_url: null,
        },
        attachments: currentNote?.attachments || [],
      };

      const notes = get().notes.map(note =>
        note.id === noteId ? updatedNote : note
      );
      set({ notes });
    } catch (error) {
      set({ error: friendlyDbError(error) });
    }
  },

  deleteNote: async (noteId: string) => {
    try {
      const { error } = await supabase
        .from('deal_notes')
        .delete()
        .eq('id', noteId);

      if (error) throw error;

      const notes = get().notes.filter(note => note.id !== noteId);
      set({ notes });
    } catch (error) {
      set({ error: friendlyDbError(error) });
    }
  },
}));
